import { useState, useCallback, useEffect } from 'react';
import { getUserChatHistory, talkToChat, RagResponseHistory } from "../Utils/Utils";
import { Vocabulary } from '../Utils/Vocabulary';
import { ragEndpoints } from '../Utils/UrlEnum';

export interface Message {
  id: string;
  content: string;
  createdAt?: Date;
  role?: 'user' | 'assistant';
}

export interface UseChatOptions {
  api?: string;
  id?: string;
  initialMessages?: Message[];
  initialInput?: string;
  onResponse?: (response: Response) => void | Promise<void>;
  onFinish?: (message: Message) => void;
  onError?: (error: Error) => void;
}

export interface UseChatReturn {
  messages: Message[];
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  error: Error | undefined;
  setError: (error: Error | undefined) => void;
  reload: () => void;
  stop: () => void;
  setMessages: (messages: Message[]) => void;
  setInput: (input: string) => void;
  chat: RagResponseHistory | undefined;
  history?: any;
  setUsedDataset: (dataset: Dataset) => void;
}
export interface Dataset {
  id: string;
  name: string;
  owner: string;
  sessionId: string;

}

export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const {
    api = ragEndpoints.ragApi,
    initialMessages = [],
    initialInput = '',
    onResponse,
    onFinish,
    onError,
  } = options;


  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState<string>(initialInput);
  const [usedDataset, setUsedDataset] = useState<Dataset>({} as Dataset);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [chat, setChat] = useState<RagResponseHistory>();

  const generateId = () => Math.random().toString(36).substring(2, 15);


  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setInput(e.target.value);
    },
    []
  );


  useEffect(() => {
    /**
     * @function fetchHistory
     * @description Fetches user chat history from local storage
     * @returns {Promise<void>}
     * @example
     * fetchHistory();
     */
    const fetchHistory = async () => {
      const userHistory = await getUserChatHistory();
      if (!userHistory || userHistory === null || userHistory === undefined) return;
      setChat(userHistory);
    };

    fetchHistory();
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!input.trim() || isLoading) return;

      const userMessage: Message = {
        id: generateId(),
        content: input.trim(),
        createdAt: new Date(),
        role: 'user',
      };

      const newMessages = [...messages, userMessage];
      setMessages(newMessages);
      setInput('');
      setIsLoading(true);
      setError(undefined);

      const controller = new AbortController();
      setAbortController(controller);

      try {
        const formData = new FormData();
        formData.append('datasetId', usedDataset.id);
        formData.append('datasetName', usedDataset.name);
        formData.append('query', newMessages[newMessages.length - 1].content);
        formData.append('sessionId', usedDataset.sessionId || "");
        formData.append('userId', localStorage.getItem('clientId') || "");

        const response = await talkToChat(api, formData);

        const assistantMessage: Message = {
          id: generateId(),
          content: response.data.answer || Vocabulary.noResponseGot,
          createdAt: new Date(),
          role: 'assistant',
        };

        const finalMessages = [...newMessages, assistantMessage];
        setMessages(finalMessages);

        if (onResponse) {
          await onResponse(response as any);
        }

        if (onFinish) {
          onFinish(assistantMessage);
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err : new Error(Vocabulary.unknownError);
        setError(errorMessage);

        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setIsLoading(false);
        setAbortController(null);
      }
    },
    [messages, input, isLoading, api, onResponse, onFinish, onError]
  );

  const reload = useCallback(() => {
    if (messages.length === 0) return;

    const lastUserMessageIndex = messages.findIndex(msg => msg.id);
    if (lastUserMessageIndex === -1) return;

    const messagesToReload = messages.slice(0, lastUserMessageIndex + 1);
    setMessages(messagesToReload);

    const lastUserMessage = messages[lastUserMessageIndex];
    setInput(lastUserMessage.content);
  }, [messages]);

  const stop = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoading(false);
    }
  }, [abortController]);

  return {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    error,
    setError,
    reload,
    stop,
    setMessages,
    setInput,
    chat,
    setUsedDataset,
  };
}
